import { getDynamicConfigVariantName } from './config-string-functions';
import {
    getCountryCodeForCountry,
    getCountryFromUrl,
    getLocaleConfigForCountryCode,
} from './url-functions';
import { getCacheBuster } from './get-cache-buster';
import { Configs } from '@sxm-core/utility-code/typescript/browser-app-configs-types';
import { tokenReplaceAppUrls } from './token-replace-app-urls';

export async function loadDefaultConfigs(): Promise<Configs> {
    const country = getCountryFromUrl(window.location.href) || 'us';
    const { variantName, variantNameForApp } = getDynamicConfigVariantName(
        window.location,
    );
    const basePath = './assets/configs';

    const cacheBuster = getCacheBuster();
    const appSettingsFile = `${country}.app-settings.json`;
    const { variant, variantForApp, ...configs } = await fetchConfigs({
        countryUrl: `${basePath}/${country}.json${cacheBuster}`,
        commonUrl: `${basePath}/common.json${cacheBuster}`,
        appSettingsUrl: `${basePath}/${appSettingsFile}${cacheBuster}`,
        variantUrl: variantName
            ? `${basePath}/variant-${variantName}/common.json${cacheBuster}`
            : null,
        variantAppUrl: variantNameForApp
            ? `${basePath}/variantapp-${variantNameForApp}/${appSettingsFile}${cacheBuster}`
            : null,
    });
    const countryCode = getCountryCodeForCountry(country) || 'us';
    const locale = getLocaleConfigForCountryCode(countryCode);

    if (variant) {
        // we will support microservice override
        if (variant.microservices) {
            // for now, it is an all or nothing
            configs.microservices = {
                ...variant.microservices,
            };
        }
        // we will support contentful override
        if (variant.contentful) {
            // for now, it is an all or nothing
            configs.contentful = {
                ...variant.contentful,
            };
        }
    }
    if (variantForApp) {
        // we will support feature flag merge
        if (variantForApp.featureFlags) {
            configs.featureFlags = {
                ...configs.featureFlags,
                ...variantForApp.featureFlags,
            };
        }
    }

    // Note: for now we are keeping Contentful settings in the clientSdkConfig
    const clientSdkConfig = configs.settings.clientSdkConfig;
    const contentful = configs.contentful;
    clientSdkConfig['baseUrlContentful'] = contentful.baseUrl;
    clientSdkConfig['contentfulSpaceId'] = contentful.spaceId;
    clientSdkConfig['contentfulEnvironmentId'] = contentful.environmentId;
    clientSdkConfig['contentfulAccessToken'] = contentful.accessToken;

    return {
        ...configs,
        countryCode,
        locale,
    };
}

async function fetchConfigs({
    countryUrl,
    commonUrl,
    appSettingsUrl,
    variantUrl,
    variantAppUrl,
}: {
    countryUrl: string;
    commonUrl: string;
    appSettingsUrl: string;
    variantUrl: string | null;
    variantAppUrl: string | null;
}) {
    const { settings, thirdParty } = await fetchConfig(countryUrl);
    const { appUrls, logging, microservices, contentful } =
        await fetchConfig(commonUrl);
    tokenReplaceAppUrls(appUrls);

    const { appSettings, featureFlags } = await fetchConfig(appSettingsUrl);
    const variant = variantUrl ? await fetchConfig(variantUrl) : null;
    const variantForApp = variantAppUrl
        ? await fetchConfig(variantAppUrl)
        : null;
    return {
        appSettings,
        microservices,
        contentful,
        settings,
        featureFlags,
        thirdParty,
        appUrls,
        logging,
        variant,
        variantForApp,
    };
}

async function fetchConfig(url: string) {
    return await fetch(url)
        .then((result) => result.json())
        .catch(() => {
            throw new Error(`Failed to load configs from ${url}!`);
        });
}
